import { memo } from 'react';

import { Loader } from 'lucide-react';

import { Progress } from '@design-system';

import { useIllustrationProgress } from '@/hooks/use-illustration-progress';
import { cn } from '@/lib/utils';

import type { FundingPolicyType } from '@shared';

interface IllustrationProgressIndicatorProps {
  isGenerating: boolean;
  fundingPolicyType: FundingPolicyType | null | undefined;
}

function IllustrationProgressIndicatorComponent({
  isGenerating,
  fundingPolicyType,
}: IllustrationProgressIndicatorProps) {
  const { elapsedSeconds, typicalSeconds, progressPercent, isOvertime } = useIllustrationProgress({
    isGenerating,
    fundingPolicyType,
  });

  if (!isGenerating) {
    return null;
  }

  const bgColor = isOvertime ? 'bg-amber-50' : 'bg-blue-50';
  const textColor = isOvertime ? 'text-amber-700' : 'text-blue-700';
  const statusText = isOvertime ? 'Still working...' : 'Generating...';
  const estimationLabel = isOvertime ? 'Taking longer than usual' : 'Estimated';
  const estimationLabelColor = isOvertime ? 'text-amber-600' : 'text-blue-500';

  return (
    <div
      className={cn('inline-flex items-center gap-3 rounded-full px-3 py-2', bgColor, textColor)}
    >
      <div className="flex items-center gap-2">
        <Loader className="h-4 w-4 shrink-0 animate-spin" />
        <span className="whitespace-nowrap text-xs font-semibold">{statusText}</span>
      </div>
      <div className="w-20">
        <Progress
          value={progressPercent}
          className={cn(
            'h-2',
            isOvertime
              ? '**:data-[slot=progress-indicator]:bg-amber-600!'
              : '**:data-[slot=progress-indicator]:bg-blue-600!',
          )}
        />
      </div>
      <div className="flex items-center gap-2 text-xs">
        <span className="whitespace-nowrap font-medium">
          {elapsedSeconds}s / ~{typicalSeconds}s
        </span>
        <span className={cn(estimationLabelColor)}>•</span>
        <span className={cn(estimationLabelColor)}>{estimationLabel}</span>
      </div>
    </div>
  );
}

export const IllustrationProgressIndicator = memo(IllustrationProgressIndicatorComponent);
